import type { AudioTrack as CustomAudioTrack } from '../typings';
import { getPlaybackTitle } from '../utils/playback';

/**
 * External API interface for video player consumers.
 */
export interface VideoPlayerExternalAPI {
  tracks: {
    /**
     * Get all available audio tracks
     * @returns Array of available audio tracks
     */
    getAudio: () => CustomAudioTrack[];

    /**
     * Get all available subtitle tracks (WebVTT)
     * @returns Array of available subtitle tracks
     */
    getSubtitles: () => TextTrack[];

    /**
     * Get the currently selected audio track
     * @returns Currently selected audio track or null if none selected
     */
    getCurrentAudio: () => CustomAudioTrack | null;

    /**
     * Get the currently selected subtitle track
     * @returns Currently selected subtitle track or null if none selected
     */
    getCurrentSubtitle: () => TextTrack | null;
  };

  resume: {
    /**
     * Check if the resume overlay should be visible
     * @returns True if resume overlay should be shown
     */
    isVisible: () => boolean;

    /**
     * Get the resume point in seconds
     * @returns Resume point time in seconds
     */
    getResumePoint: () => number;

    /**
     * Get the video title
     * @returns Video title string (empty string if unavailable)
     */
    getVideoTitle: () => string;
  };

  controls: {
    /**
     * Set the audio track by track ID
     * @param trackId - The ID of the audio track to select
     */
    setAudioTrack: (trackId: string) => void;

    /**
     * Set the subtitle track by track ID (or null to disable)
     * @param trackId - The ID of the subtitle track to select, or null to disable subtitles
     */
    setSubtitleTrack: (trackId: string | null) => void;

    /**
     * Enable or disable user input handling
     * @param enabled - When false, the player will ignore user input events (useful when showing modals/overlays)
     */
    setInputEnabled: (enabled: boolean) => void;

    /**
     * Resume playback from the saved resume point
     */
    resumePlayback: () => void;

    /**
     * Restart playback from the beginning
     */
    restartPlayback: () => void;

    /**
     * Dismiss the resume overlay without taking action
     */
    dismissResumeOverlay: () => void;
  };
}

/**
 * Store instance type for dependency injection
 */
export type StoreInstance = {
  getState: () => any;
  getActions: () => any;
};

/**
 * Creates a external API instance that maps to the internal store.
 * This function provides an abstraction layer between the internal store
 * implementation and external consumers.
 *
 * @param storeInstance - The store instance to use for state and actions
 * @returns VideoPlayerExternalAPI - API object for external consumption
 */
export function createExternalAPI(storeInstance: StoreInstance): VideoPlayerExternalAPI {
  return {
    tracks: {
      getAudio: (): CustomAudioTrack[] => {
        return storeInstance.getState().player.audioTracks as unknown as CustomAudioTrack[];
      },

      getSubtitles: (): TextTrack[] => {
        return storeInstance.getState().subtitles.webVttSubtitles;
      },

      getCurrentAudio: (): CustomAudioTrack | null => {
        return storeInstance.getState().player.audioTrack as unknown as CustomAudioTrack | null;
      },

      getCurrentSubtitle: (): TextTrack | null => {
        return storeInstance.getState().subtitles.activeSubtitle;
      },
    },

    resume: {
      isVisible: (): boolean => {
        return storeInstance.getState().player.resumeOverlayVisible;
      },

      getResumePoint: (): number => {
        return storeInstance.getState().player.playbackResumePoint;
      },

      getVideoTitle: (): string => {
        const videoData = storeInstance.getState().videos.data;
        return videoData ? getPlaybackTitle(videoData) : '';
      },
    },

    controls: {
      setAudioTrack: (id: string): void => {
        const audioTracks = storeInstance.getState().player.audioTracks;
        const track = audioTracks.find((t: any) => t.id.toString() === id);

        if (track) {
          storeInstance.getActions().player.setAudioTrack(track);
          storeInstance.getActions().settings.setAudioTrack(track);
        }
      },

      setSubtitleTrack: (id: string | null): void => {
        const subtitles = storeInstance.getState().subtitles.webVttSubtitles;
        const track = id ? subtitles.find((t: any) => t.id === id) : null;

        storeInstance.getActions().subtitles.setActiveSubtitle(track || null);
        storeInstance.getActions().settings.setSubtitleTrack(track || null);
      },

      setInputEnabled: (enabled: boolean): void => {
        storeInstance.getActions().player.setInputEnabled(enabled);
      },

      resumePlayback: (): void => {
        storeInstance.getActions().player.resumePlayback();
      },

      restartPlayback: (): void => {
        storeInstance.getActions().player.restartPlayback();
      },

      dismissResumeOverlay: (): void => {
        storeInstance.getActions().player.setResumeOverlayVisible(false);
      },
    },
  };
}
